import React, { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"
import { listResourceDocuments, uploadMustReadFile } from "helpers/mustReadApi"
import HtmlContentFrame from "pages/MustRead/HtmlContentFrame"

const TYPES = [
  { value: "pdf", label: "PDF" },
  { value: "video", label: "Video" },
  { value: "html", label: "HTML" },
]

const ACCEPT = { pdf: "application/pdf,.pdf", video: "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" }

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 3000 }),
  menu: (base) => ({ ...base, backgroundColor: "#fff", zIndex: 3000 }),
  menuList: (base) => ({ ...base, backgroundColor: "#fff" }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused || state.isSelected ? "#e8f0f8" : "#fff",
    color: "#1f2d3d",
  }),
}

const matchesType = (doc, type) =>
  type === "pdf" ? doc.mimeType === "application/pdf" : String(doc.mimeType || "").startsWith("video/")

const emptyValues = {
  title: "",
  description: "",
  contentType: "pdf",
  documentId: null,
  documentName: null,
  html: "",
  videoDurationSeconds: null,
  audienceAll: false,
  companyIds: [],
  changeNote: "",
}

function readVideoDuration(file) {
  return new Promise((resolve) => {
    const video = document.createElement("video")
    video.preload = "metadata"
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src)
      resolve(Number.isFinite(video.duration) ? Math.round(video.duration) : null)
    }
    video.onerror = () => resolve(null)
    video.src = URL.createObjectURL(file)
  })
}

const MustReadItemModal = ({ isOpen, row, companies, onCancel, onSave }) => {
  const isNew = !row
  const [values, setValues] = useState(emptyValues)
  const [source, setSource] = useState("existing")
  const [documents, setDocuments] = useState([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return
    setError(null)
    setSource("existing")
    setValues(
      row
        ? {
            ...emptyValues,
            ...row,
            description: row.description || "",
            html: row.html || "",
            changeNote: row.changeNote || "",
          }
        : emptyValues
    )
    listResourceDocuments().then(setDocuments).catch(() => setDocuments([]))
  }, [isOpen, row])

  const set = (patch) => setValues((v) => ({ ...v, ...patch }))

  const docOptions = useMemo(
    () =>
      documents
        .filter((d) => matchesType(d, values.contentType))
        .map((d) => ({ value: d.id, label: d.name || d.fileName })),
    [documents, values.contentType]
  )

  const companyOptions = useMemo(() => {
    const pinned = companies.filter((c) => c.isPublic || c.isDistributor)
    const rest = companies.filter((c) => !c.isPublic && !c.isDistributor)
    const label = (c) => (c.isPublic ? "Public" : c.isDistributor ? "Distributors" : c.name)
    return [...pinned, ...rest].map((c) => ({ value: c.id, label: label(c) }))
  }, [companies])

  const handleUpload = async (file) => {
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const duration = values.contentType === "video" ? await readVideoDuration(file) : null
      const doc = await uploadMustReadFile(file)
      set({ documentId: doc.documentId, documentName: doc.name, videoDurationSeconds: duration })
    } catch (e) {
      setError(e.message || "Upload failed")
    } finally {
      setUploading(false)
    }
  }

  const validate = () => {
    if (!values.title.trim()) return "Title is required"
    if (values.contentType === "html" ? !values.html.trim() : !values.documentId) {
      return values.contentType === "html" ? "HTML content is required" : "Choose or upload a file"
    }
    if (!values.audienceAll && values.companyIds.length === 0) return "Choose an audience"
    return null
  }

  const submit = () => {
    const message = validate()
    if (message) {
      setError(message)
      return
    }
    const contentChanged =
      isNew ||
      (values.contentType === "html"
        ? (values.html || "") !== (row.html || "")
        : values.documentId !== row.documentId)
    onSave(
      {
        ...values,
        title: values.title.trim(),
        description: values.description.trim() || null,
        changeNote: values.changeNote.trim() || null,
        companyIds: values.audienceAll ? [] : values.companyIds,
      },
      { contentChanged }
    )
  }

  return (
    <Modal isOpen={isOpen} toggle={onCancel} size="lg" scrollable>
      <ModalHeader toggle={onCancel}>{isNew ? "Add Onboarding resource" : "Edit Onboarding resource"}</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        <FormGroup>
          <Label for="mr-title">Title</Label>
          <Input id="mr-title" value={values.title} onChange={(e) => set({ title: e.target.value })} />
        </FormGroup>
        <FormGroup>
          <Label for="mr-description">Description</Label>
          <Input id="mr-description" type="textarea" rows={2} value={values.description} onChange={(e) => set({ description: e.target.value })} />
        </FormGroup>

        <FormGroup tag="fieldset" disabled={!isNew}>
          <legend className="col-form-label pt-0">Type</legend>
          <div className="d-flex gap-3">
            {TYPES.map((t) => (
              <FormGroup check key={t.value}>
                <Input
                  id={`mr-type-${t.value}`}
                  type="radio"
                  name="mr-type"
                  checked={values.contentType === t.value}
                  onChange={() => set({ contentType: t.value, documentId: null, documentName: null })}
                />
                <Label check for={`mr-type-${t.value}`}>{t.label}</Label>
              </FormGroup>
            ))}
          </div>
          {!isNew && <small className="text-muted">Type can’t change after publishing. Retire and add a new doc instead.</small>}
        </FormGroup>

        {values.contentType === "html" ? (
          <>
            <FormGroup>
              <Label for="mr-html">HTML content</Label>
              <Input
                id="mr-html"
                type="textarea"
                rows={10}
                style={{ fontFamily: "monospace", fontSize: 13 }}
                value={values.html}
                onChange={(e) => set({ html: e.target.value })}
              />
              <small className="text-muted">Scripts are disabled when users view this page.</small>
            </FormGroup>
            {values.html.trim() && (
              <div className="mb-3">
                <Label>Preview</Label>
                <HtmlContentFrame html={values.html} />
              </div>
            )}
          </>
        ) : (
          <FormGroup>
            <div className="d-flex gap-3 mb-2">
              <FormGroup check>
                <Input id="mr-src-existing" type="radio" checked={source === "existing"} onChange={() => setSource("existing")} />
                <Label check for="mr-src-existing">Choose existing resource</Label>
              </FormGroup>
              <FormGroup check>
                <Input id="mr-src-upload" type="radio" checked={source === "upload"} onChange={() => setSource("upload")} />
                <Label check for="mr-src-upload">Upload new</Label>
              </FormGroup>
            </div>
            {source === "existing" ? (
              <Select
                inputId="mr-doc"
                aria-label="Resource"
                classNamePrefix="select"
                options={docOptions}
                value={values.documentId ? { value: values.documentId, label: values.documentName || "Selected file" } : null}
                onChange={(o) => set({ documentId: o?.value || null, documentName: o?.label || null })}
                placeholder={`Search ${values.contentType === "pdf" ? "PDF" : "video"} resources…`}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={selectStyles}
              />
            ) : (
              <div className="d-flex align-items-center gap-2">
                <Input type="file" accept={ACCEPT[values.contentType]} onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} />
                {uploading && <Spinner size="sm" />}
              </div>
            )}
            {values.documentName && <small className="text-muted d-block mt-1">Current file: {values.documentName}</small>}
          </FormGroup>
        )}

        <FormGroup>
          <Label>Audience</Label>
          <FormGroup switch>
            <Input id="mr-all" type="switch" checked={values.audienceAll} onChange={(e) => set({ audienceAll: e.target.checked })} />
            <Label check for="mr-all">All users</Label>
          </FormGroup>
          {!values.audienceAll && (
            <Select
              isMulti
              aria-label="Companies"
              classNamePrefix="select"
              options={companyOptions}
              value={companyOptions.filter((o) => values.companyIds.includes(o.value))}
              onChange={(opts) => set({ companyIds: (opts || []).map((o) => o.value) })}
              placeholder="Public, Distributors, or companies…"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={selectStyles}
            />
          )}
        </FormGroup>

        {!isNew && (
          <FormGroup>
            <Label for="mr-change">What changed</Label>
            <Input id="mr-change" value={values.changeNote} onChange={(e) => set({ changeNote: e.target.value })} placeholder="Shown to users and in the email" />
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onCancel}>Cancel</Button>
        <Button className="mr-btn-primary" onClick={submit} disabled={uploading}>Stage change</Button>
      </ModalFooter>
    </Modal>
  )
}

export default MustReadItemModal
